// src/components/AssistantPanel.jsx
// Botón flotante + panel de chat con el asistente IA.
// Visible: función 40 (acceso al asistente).
// Modo experto: función 39 (administradores).

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTexts, TXT } from '../context/TextContext';
import api from '../services/api';

const FUNCTION_ASISTENTE = 40;
const FUNCTION_EXPERTO   = 39;

function MensajeIA({ texto }) {
  // Renderiza markdown básico: negrita, cursiva, listas, líneas
  const lines = texto.split('\n');
  return (
    <div style={{ lineHeight: 1.55 }}>
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        // Lista con bullet
        if (/^[\-•*] /.test(line)) {
          return (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
              <span style={{ color: 'var(--color1,#2a9d8f)', marginTop: 2 }}>•</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^[\-•*] /, '')) }} />
            </div>
          );
        }
        // Numerada
        if (/^\d+\. /.test(line)) {
          const [num, ...rest] = line.split('. ');
          return (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
              <span style={{ color: 'var(--color1,#2a9d8f)', minWidth: 16, textAlign: 'right' }}>{num}.</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(rest.join('. ')) }} />
            </div>
          );
        }
        // Título ##
        if (/^##+ /.test(line)) {
          return <p key={i} style={{ fontWeight: 600, marginBottom: 4, marginTop: 8, color: '#2a4542' }} dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^##+ /, '')) }} />;
        }
        return <p key={i} style={{ marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />;
      })}
    </div>
  );
}

function formatInline(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,     '<em>$1</em>')
    .replace(/`(.+?)`/g,       '<code style="background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:0.9em">$1</code>');
}

export default function AssistantPanel() {
  const { user }   = useAuth();
  const { t, idiId } = useTexts();

  const [open,      setOpen]      = useState(false);
  const [mensajes,  setMensajes]  = useState([]);   // [{role:'user'|'ia', text, loading?}]
  const [input,     setInput]     = useState('');
  const [cargando,  setCargando]  = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  const isExperto  = (user?.functions || []).includes(FUNCTION_EXPERTO);

  const TX = {
    titulo:        t(TXT.ASSISTANT,             'Asistente'),
    experto:       t(TXT.EXPERT_MODE,           'Modo experto'),
    placeholder:   t(TXT.ASSISTANT_PLACEHOLDER, '¿En qué te ayudo?'),
    send:          t(TXT.SEND,                  'Enviar'),
    error:         t(TXT.ASSISTANT_ERROR,       'Error, intentá de nuevo'),
    bienvenida:    t(TXT.ASSISTANT_WELCOME,     '¡Hola! Soy el asistente de Recibido. Preguntame sobre el sistema: cómo realizar tareas, qué menú usar o cómo está organizada la información.'),
    bienvenidaExp: t(TXT.ASSISTANT_WELCOME_EXP, '¡Hola! Estoy en modo experto. Puedo orientarte y también crear filtros, reglas de notificación y más.'),
  };

  // Mensaje de bienvenida al abrir por primera vez
  useEffect(() => {
    if (open && mensajes.length === 0) {
      setMensajes([{ role: 'ia', text: isExperto ? TX.bienvenidaExp : TX.bienvenida }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  const historialParaApi = () =>
    mensajes
      .filter(m => !m.loading && m.role !== 'ia' || m.role === 'ia')
      .map(m => ({ role: m.role === 'ia' ? 'assistant' : 'user', content: m.text }));

  const enviar = async () => {
    const texto = input.trim();
    if (!texto || cargando) return;

    const nuevos = [...mensajes, { role: 'user', text: texto }];
    setMensajes([...nuevos, { role: 'ia', loading: true, text: '' }]);
    setInput('');
    setCargando(true);

    try {
      const { data } = await api.post('/asistente', {
        mensaje:   texto,
        historial: historialParaApi(),
        idioma:    idiId === 1 ? 'en' : 'es',
      });
      setMensajes([...nuevos, { role: 'ia', text: data.respuesta }]);
    } catch {
      setMensajes([...nuevos, { role: 'ia', text: TX.error, error: true }]);
    } finally {
      setCargando(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  const tieneAsistente = (user?.functions || []).includes(FUNCTION_ASISTENTE);

  if (!user || !tieneAsistente) return null;

  return (
    <>
      {/* ── Panel de chat ── */}
      {open && (
        <div style={styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/images/asistente.gif" alt="" style={{ width: 32, height: 32, borderRadius: 8 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#fff' }}>{TX.titulo}</div>
                {isExperto && (
                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.04em' }}>
                    ⚡ {TX.experto}
                  </div>
                )}
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={styles.closeBtn} title="Cerrar">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {/* Mensajes */}
          <div style={styles.messages}>
            {mensajes.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                {m.role === 'ia' && (
                  <img src="/images/asistente.gif" alt="" style={{ width: 24, height: 24, borderRadius: 6, marginRight: 8, flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }} />
                )}
                <div style={{
                  ...styles.bubble,
                  ...(m.role === 'user' ? styles.bubbleUser : styles.bubbleIA),
                  ...(m.error ? { background: '#fff0f0', color: '#c00' } : {}),
                }}>
                  {m.loading
                    ? <span className="whl-dots" style={styles.dots}><span /><span /><span /></span>
                    : m.role === 'ia'
                      ? <MensajeIA texto={m.text} />
                      : m.text
                  }
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={styles.inputArea}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={TX.placeholder}
              rows={1}
              style={styles.textarea}
              disabled={cargando}
            />
            <button onClick={enviar} disabled={cargando || !input.trim()} style={styles.sendBtn}>
              <i className="fa-solid fa-paper-plane" />
            </button>
          </div>
        </div>
      )}

      {/* ── Botón flotante ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...styles.fab, ...(open ? styles.fabOpen : {}) }}
        title={TX.titulo}
      >
        <img src="/images/asistente.gif" alt={TX.titulo} style={{ width: 56, height: 56, borderRadius: 16, display: 'block' }} />
        {isExperto && !open && (
          <span style={styles.expertBadge}>⚡</span>
        )}
      </button>
    </>
  );
}

const styles = {
  fab: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: '16px',
    border: 'none',
    background: 'transparent',
    boxShadow: '0 4px 20px rgba(110,127,255,0.45)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    transition: 'transform 0.2s, box-shadow 0.2s',
    padding: 0,
    overflow: 'hidden',
  },
  fabOpen: {
    transform: 'scale(0.92)',
    boxShadow: '0 2px 10px rgba(110,127,255,0.3)',
  },
  expertBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    fontSize: '0.65rem',
    background: '#f59e0b',
    borderRadius: '50%',
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  panel: {
    position: 'fixed',
    bottom: 90,
    right: 24,
    width: 360,
    maxHeight: 520,
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1049,
    overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.08)',
  },
  header: {
    background: 'linear-gradient(135deg, #6e7fff 0%, #8b5cf6 100%)',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '2px 6px',
    borderRadius: 4,
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 12px',
    minHeight: 0,
  },
  bubble: {
    maxWidth: '82%',
    padding: '9px 12px',
    borderRadius: 12,
    fontSize: '0.83rem',
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  bubbleUser: {
    background: 'linear-gradient(135deg, #6e7fff 0%, #8b5cf6 100%)',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  bubbleIA: {
    background: '#f5f5f7',
    color: '#2a2a2a',
    borderBottomLeftRadius: 4,
  },
  inputArea: {
    borderTop: '1px solid #eee',
    padding: '10px 12px',
    display: 'flex',
    gap: 8,
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    resize: 'none',
    border: '1.5px solid #e0e0e0',
    borderRadius: 10,
    padding: '8px 10px',
    fontSize: '0.83rem',
    fontFamily: 'inherit',
    outline: 'none',
    lineHeight: 1.45,
    maxHeight: 100,
    overflowY: 'auto',
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #6e7fff 0%, #8b5cf6 100%)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    opacity: 1,
    transition: 'opacity 0.15s',
  },
  dots: {
    display: 'inline-flex',
    gap: 4,
    alignItems: 'center',
    height: 18,
  },
};
